import { formatBreakEven, formatMoney, formatPercent } from "@shared/roi/format";
import type { ScenarioVersionRow } from "@shared/schemas/scenario";
import { Badge } from "@client/components/Badge";
import { formatDateTime, formatRelative } from "@client/lib/datetime";

/**
 * The history of one scenario, newest first.
 *
 * Every row is a button that selects the version for the diff beside it, so the
 * list behaves like a set of radios and says so to a screen reader: the group is
 * a `radiogroup`, each row is a `radio`, and arrow keys move through it the way
 * the browser already handles for that role.
 *
 * Each row carries the ROI the save actually produced. That figure is what
 * people scan a history for — "which of these was the 41% one" — and it is read
 * from the stored summary rather than recomputed, because it is the number that
 * save recorded, not the number today's engine would produce from it.
 */

interface VersionTimelineProps {
  versions: readonly ScenarioVersionRow[];
  /** The scenario's live version, which is what the top row will normally be. */
  currentVersion: number;
  selected: number | null;
  onSelect: (version: number) => void;
  currencyCode: string;
  horizonYears: number;
}

export function VersionTimeline({
  versions,
  currentVersion,
  selected,
  onSelect,
  currencyCode,
  horizonYears,
}: VersionTimelineProps) {
  return (
    <ol role="radiogroup" aria-label="Saved versions" className="flex flex-col gap-1.5">
      {versions.map((version) => {
        const isSelected = version.version === selected;
        const isCurrent = version.version === currentVersion;
        const summary = version.summary;

        return (
          <li key={version.id}>
            <button
              type="button"
              role="radio"
              aria-checked={isSelected}
              onClick={() => onSelect(version.version)}
              className={`w-full rounded-lg border px-3 py-2.5 text-left transition ${
                isSelected ? "border-azure-deep bg-chip-bg" : "border-line hover:bg-link-bg-hover"
              }`}
            >
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="tabular text-sm font-bold text-ink">v{version.version}</span>
                {isCurrent && <Badge kind="info">Current</Badge>}
                {version.pinned && (
                  <Badge kind="neutral" title="Kept regardless of age">
                    Pinned
                  </Badge>
                )}
                {version.label && (
                  <span className="min-w-0 truncate text-sm text-ink">{version.label}</span>
                )}
              </div>

              <p className="mt-0.5 text-xs text-ink-soft">
                {version.createdBy.fullName} ·{" "}
                <time dateTime={version.createdAt} title={formatDateTime(version.createdAt)}>
                  {formatRelative(version.createdAt)}
                </time>
              </p>

              {summary && (
                <p className="tabular mt-1 flex flex-wrap gap-x-3 text-xs text-ink-soft">
                  <span>
                    ROI{" "}
                    <span className="font-semibold text-ink">{formatPercent(summary.roiPct)}</span>
                  </span>
                  <span>{formatBreakEven(summary.breakEvenYears, horizonYears)}</span>
                  <span
                    style={{ color: summary.net >= 0 ? "var(--roi-revenue)" : "var(--roi-cost)" }}
                    className="font-semibold"
                  >
                    {formatMoney(currencyCode, summary.net, { compact: true, signed: true })}
                  </span>
                </p>
              )}
            </button>
          </li>
        );
      })}
    </ol>
  );
}
